import { create } from 'zustand'

import { useQueryClient } from '@tanstack/react-query'

import { CHAIN_IDS, ChainName, NATIVE_TOKEN_ADDRESS, getChainNameFromChainId } from '@src/configs/chainInfo'
import { ALL_BALANCES_QUERY_KEY } from '@src/hooks/useFetchAllBalances'
import { IBalanceResponse } from '@src/types/balance'
import { IToken } from '@src/types/token'
import { computeUSDAmount } from '@src/utils/balance'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('TokenStore')

interface TokensStore {
    tokens: IToken[]
    nativeTokenPricesInUSD: Record<number, number>
    isLoading: boolean
    error: string | null
    getGasTokenProps: (key: string | number) => IToken

    // Setters
    setTokens: (tokens: IToken[]) => void
    setNativeTokenPricesInUSD: (prices: Record<number, number>) => void
    setIsLoading: (loading: boolean) => void
    setError: (error: string | null) => void

    /**
     * Updates all tokens with user balances.
     * NOTE: This function erases all previously existing balances, meaning that if an empty list is passed,
     * all tokens will lose balance data.
     * @param data IBalanceResponse[] the list of balances to use.
     * @returns void
     */
    resetTokenBalancesWithBalances: (data: IBalanceResponse[]) => void

    /**
     * Updates specific token balances based on the list provided. Leaves other balances untouched.
     * @param data IBalanceResponse[] the list of balances to use.
     * @returns void
     */
    updateTokenBalancesWithBalances: (data: IBalanceResponse[]) => void

    updateTokenBalancesForChain: (chain: string, balances: IBalanceResponse[]) => void
}

export const useTokensStore = create<TokensStore>((set, get) => ({
    tokens: [],
    nativeTokenPricesInUSD: {},
    isLoading: false,
    error: null,
    getGasTokenProps: (key: string | number): IToken => {
        const tokens = get().tokens
        let chainId

        if (typeof key === 'string' || key in CHAIN_IDS) {
            chainId = CHAIN_IDS[key as ChainName]
        } else {
            chainId = key
        }

        const result = tokens.find(t => t.chainId === chainId && t.address === NATIVE_TOKEN_ADDRESS[chainId])

        if (result) {
            return result
        } else {
            logger.error(`Cannot find gas token props for key ${key}. Result not found in tokens list.`)
            return {} as IToken
        }
    },

    setTokens: tokens => set({ tokens }),
    setNativeTokenPricesInUSD: prices => set({ nativeTokenPricesInUSD: prices }),
    setIsLoading: loading => set({ isLoading: loading }),
    setError: error => set({ error }),

    resetTokenBalancesWithBalances: (data: IBalanceResponse[]) => {
        logger.info('resetting user balances with data', { data })

        const resetBalance = (token: IToken, bList: IBalanceResponse[]): IToken => {
            const balanceData = bList.find(
                b => b.address === token.address && getChainNameFromChainId(token.chainId) === b.chain
            )

            if (balanceData) {
                return {
                    ...token,
                    userBalance: BigInt(balanceData.balance),
                    userBalanceInUSD: computeUSDAmount(token, BigInt(balanceData.balance)),
                }
            } else {
                return {
                    ...token,
                    userBalance: undefined, // Explicitly reset to undefined
                    userBalanceInUSD: undefined, // Explicitly reset to undefined
                }
            }
        }

        set({ tokens: get().tokens.map(t => resetBalance(t, data)) })

        logger.info('tokens after resetting', { tokens: get().tokens })
    },

    updateTokenBalancesWithBalances: (data: IBalanceResponse[]) => {
        logger.info('updating user balances with data', { data })

        const updateBalance = (token: IToken, bList: IBalanceResponse[]): IToken => {
            const balanceData = bList.find(
                b => b.address === token.address && getChainNameFromChainId(token.chainId) === b.chain
            )

            if (balanceData) {
                return {
                    ...token,
                    userBalance: BigInt(balanceData.balance),
                    userBalanceInUSD: computeUSDAmount(token, BigInt(balanceData.balance)),
                }
            } else {
                return token // Leave all properties alone.
            }
        }

        set({
            tokens: get().tokens.map(t => updateBalance(t, data)),
        })

        logger.info('tokens after updating', { tokens: get().tokens })
    },

    updateTokenBalancesForChain: (chain, balances) =>
        set(state => ({
            tokens: state.tokens.map(token => ({
                ...token,
                userBalance: balances.find(b => b.address === token.address)?.balance
                    ? BigInt(balances.find(b => b.address === token.address)!.balance)
                    : token.userBalance,
            })),
        })),
}))

// Create a custom hook to invalidate balances
export function useInvalidateBalances() {
    const queryClient = useQueryClient()

    return () => {
        // This will invalidate all queries that start with ALL_BALANCES_QUERY_KEY
        // regardless of what other query key parts they have
        queryClient.invalidateQueries({
            queryKey: [ALL_BALANCES_QUERY_KEY],
            // Use exact: false to match any query that starts with the key
            exact: false,
        })
    }
}
